import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
} from "reactstrap";
import Select from "react-select";
import { getCampaigns, saveCampaign, deleteCampaign } from "../../components/Api/GmsCampaign";
import { getSources } from "../../components/Api/GmsSource";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { SquarePen, Plus, Trash2, Calendar, DollarSign, Info, User, Clock, Building, MapPin, Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

// Session management
const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
      businessId: authUser.companyId || user.user_Company_Id || null,
      branchId: authUser.branchId || user.user_Branch_Id || null,
    };
};

const defaultForm = {
    CampaignId: 0,
    CampaignName: "",
    SourceId: null,
    Details: "",
    ValidFrom: new Date(),
    ValidTo: new Date(new Date().setMonth(new Date().getMonth() + 1)),
    Amount: 0,
    BusinessId: null,
    BranchId: null,
    CreatedBy: 1,
    CreatedOn: new Date().toISOString(),
    ModifiedBy: null,
    ModifiedOn: null,
};

export default function CampaignPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [sources, setSources] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]); // Filtered for form
  const [allBranches, setAllBranches] = useState([]); // All for lookup

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsCampaign');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsCampaign');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsCampaign');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsCampaign');

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadCampaigns();
      loadSources();
      loadDropdowns();
      loadAllBranchesLookup();
    } else {
      // Avoid infinite redirect if already on Dashboard
      if (window.location.pathname !== '/Dashboard') {
        window.location.href = '/Dashboard';
      }
    }
  }, [hasPermission]);

  useEffect(() => {
    if (form.BusinessId) {
      loadBranchesForBusiness(form.BusinessId);
    } else {
      setBranches([]);
    }
  }, [form.BusinessId]);

  useEffect(() => {
    if (page > 0 && hasPermission) {
      loadCampaigns();
    }
  }, [page]);

  const loadDropdowns = async () => {
    try {
      const bData = await getBusinesses({ business_Active: true });
      const businessesData = Array.isArray(bData) ? bData : (bData?.Businesses || bData?.businesses || bData?.data || []);
      setBusinesses(businessesData);
      
      if (session.businessId && !form.BusinessId && !isEditing) {
        setForm(prev => ({ ...prev, BusinessId: session.businessId }));
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadAllBranchesLookup = async () => {
    try {
      // Load all branches for display lookup
      // If user is restricted to a business, load branches for that business
      const params = session.businessId ? { Branch_Buisness_Id: session.businessId, branch_Active: true } : { branch_Active: true };
      const response = await getBranches(params);
      const branchesData = Array.isArray(response) ? response : (response?.Branches || response?.branches || response?.data || []);
      setAllBranches(branchesData);
    } catch (error) {
      console.error("Error loading lookup branches:", error);
    }
  };

  const loadBranchesForBusiness = async (businessId) => {
    try {
      const response = await getBranches({ Branch_Buisness_Id: businessId, branch_Active: true });
      const branchesData = Array.isArray(response) ? response : (response?.Branches || response?.branches || response?.data || []);
      setBranches(branchesData);
      
      if (session.branchId && !form.BranchId && !isEditing) {
        const branchInList = branchesData.find(b => (b.branch_Id || b.Branch_Id) === session.branchId);
        if (branchInList) {
          setForm(prev => ({ ...prev, BranchId: session.branchId }));
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadSources = async () => {
    try {
      // Get all sources for dropdown (passing dummy pagination to get all if possible, or just a large PageSize)
      const response = await getSources({ Page: 1, PageSize: 1000 });
      const sourcesData = Array.isArray(response) ? response : (response?.Sources || response?.sources || response?.data || []);
      setSources(sourcesData);
    } catch (error) {
      console.error("Error loading sources:", error);
    }
  };

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const filter = {
        Page: page,
        PageSize: pageSize,
      };
      const response = await getCampaigns(filter);
      
      const campaignsData = Array.isArray(response) ? response : (response?.Campaigns || response?.campaigns || response?.data || []);
      const count = response?.TotalCount || response?.totalCount || (Array.isArray(response) ? response.length : campaignsData.length);
      
      setCampaigns(campaignsData);
      setTotalCount(count);
    } catch (error) {
      console.error("Error loading campaigns:", error);
      setCampaigns([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.BusinessId) {
      Swal.fire("Error", "Please select Company", "error");
      return;
    }
    if (!form.BranchId) {
      Swal.fire("Error", "Please select Branch", "error");
      return;
    }
    if (!form.CampaignName || !form.CampaignName.trim()) {
      Swal.fire("Error", "Please enter campaign name", "error");
      return;
    }
    if (!form.SourceId) {
      Swal.fire("Error", "Please select source", "error");
      return;
    }
    if (form.Amount < 0) {
      Swal.fire("Error", "Amount cannot be negative", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const formData = {
        ...form,
        ValidFrom: Array.isArray(form.ValidFrom) ? form.ValidFrom[0].toISOString() : new Date(form.ValidFrom).toISOString(),
        ValidTo: Array.isArray(form.ValidTo) ? form.ValidTo[0].toISOString() : new Date(form.ValidTo).toISOString(),
        CreatedBy: isEditing ? form.CreatedBy : session.userId,
        ModifiedBy: isEditing ? session.userId : null,
      };
      
      const result = await saveCampaign(formData);
      
      if (result.id && result.id > 0) {
        Swal.fire("Success", result.message || "Campaign saved successfully", "success");
        setForm({
            ...defaultForm,
            BusinessId: session.businessId,
            BranchId: session.branchId
        });
        setIsEditing(false);
        loadCampaigns();
        // Stay on Tab 1 as requested
      } else {
        Swal.fire("Error", result.message || "Failed to save campaign", "error");
      }
    } catch (error) {
      console.error("Error saving campaign:", error);
      Swal.fire("Error", "Failed to save campaign: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (camp) => {
    setForm({
        CampaignId: camp.campaignId || camp.CampaignId || 0,
        CampaignName: camp.campaignName || camp.CampaignName || "",
        SourceId: camp.sourceId || camp.SourceId || null,
        Details: camp.details || camp.Details || "",
        ValidFrom: new Date(camp.validFrom || camp.ValidFrom),
        ValidTo: new Date(camp.validTo || camp.ValidTo),
        Amount: camp.amount || camp.Amount || 0,
        BusinessId: camp.businessId || camp.BusinessId || null,
        BranchId: camp.branchId || camp.BranchId || null,
        CreatedBy: camp.createdBy || camp.CreatedBy || 1,
        CreatedOn: camp.createdOn || camp.CreatedOn || new Date().toISOString(),
    });
    setIsEditing(true);
    setActiveTab("1");
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
     Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await deleteCampaign(id);
          Swal.fire("Deleted!", res.message || "Campaign has been deleted.", "success");
          loadCampaigns();
        } catch (error) {
          Swal.fire("Error", "Failed to delete campaign", "error");
        }
      }
    });
  };

  const handleCancelEdit = () => {
    setForm({
        ...defaultForm,
        BusinessId: session.businessId,
        BranchId: session.branchId
    });
    setIsEditing(false);
  };

  const handleViewDetails = (camp) => {
    const details = camp.details || camp.Details || "No details provided";
    Swal.fire({
      title: `<span class="text-primary">${camp.campaignName || camp.CampaignName}</span>`,
      html: `
        <div class="text-start p-2">
            <h6 class="fw-bold border-bottom pb-2 mb-3">Campaign Details</h6>
            <div style="white-space: pre-wrap; font-size: 0.9rem; max-height: 400px, overflow-y: auto;">
                ${details}
            </div>
        </div>
      `,
      confirmButtonText: "Close",
      confirmButtonColor: "#556ee6",
      width: '500px'
    });
  };

  if (!hasPermission) {
    return null;
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  const getSourceName = (id) => {
      const source = sources.find(s => (s.Source_Id || s.source_Id) === id);
      return source ? (source.SourceName || source.sourceName) : id;
  };

  const getBusinessName = (id) => {
      const bus = businesses.find(b => (b.business_Id || b.Business_Id) === id);
      return bus ? (bus.business_NameEn || bus.Business_NameEn) : id;
  };

  const getBranchName = (id) => {
      const branch = allBranches.find(b => (b.branch_Id || b.Branch_Id) === id);
      return branch ? (branch.branch_NameEn || branch.Branch_NameEn) : id;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Campaign Management" breadcrumbItem="GMS" />
          
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <div className="wizard clearfix">
                    <div className="steps clearfix">
                      <ul className="nav-tabs-custom nav">
                        {!isViewOnly && (
                          <NavItem className={classnames({ current: activeTab === "1" })}>
                            <NavLink
                              className={classnames({ current: activeTab === "1" })}
                              onClick={() => setActiveTab("1")}
                              style={{ cursor: "pointer" }}
                            >
                              <span className="number">1.</span> {isEditing ? "Edit Campaign" : "Add Campaign"}
                            </NavLink>
                          </NavItem>
                        )}
                        <NavItem className={classnames({ current: activeTab === "2" })}>
                          <NavLink
                            className={classnames({ active: activeTab === "2" })}
                            onClick={() => setActiveTab("2")}
                            style={{ cursor: "pointer" }}
                          >
                            <span className="number">2.</span> Campaign List
                          </NavLink>
                        </NavItem>
                      </ul>
                    </div>

                    <div className="content clearfix mt-4">
                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                          {!isViewOnly && (
                            <div className="p-2">
                                    {isEditing && (
                                        <h5 className="card-title mb-4 text-primary">
                                            Edit Campaign Details
                                        </h5>
                                    )}
                                <Form onSubmit={handleSubmit} className="needs-validation">
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Company <span className="text-danger">*</span></Label>
                                                <Select
                                                    options={businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn }))}
                                                    value={businesses.filter(b => b.business_Id === form.BusinessId).map(b => ({ value: b.business_Id, label: b.business_NameEn }))[0] || null}
                                                    onChange={(opt) => handleFormChange("BusinessId", opt?.value || null)}
                                                    styles={selectStyles}
                                                    placeholder="Select Company"
                                                    isClearable
                                                    isDisabled={!!session.businessId}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Branch <span className="text-danger">*</span></Label>
                                                <Select
                                                    options={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))}
                                                    value={branches.filter(b => b.branch_Id === form.BranchId).map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))[0] || null}
                                                    onChange={(opt) => handleFormChange("BranchId", opt?.value || null)}
                                                    styles={selectStyles}
                                                    placeholder="Select Branch"
                                                    isClearable
                                                    isDisabled={!!session.branchId}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Campaign Name <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="text"
                                                    value={form.CampaignName}
                                                    onChange={(e) => handleFormChange("CampaignName", e.target.value)}
                                                    placeholder="Enter campaign name"
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Source <span className="text-danger">*</span></Label>
                                                <Select
                                                    options={sources.map(s => ({ value: s.Source_Id || s.source_Id, label: s.SourceName || s.sourceName }))}
                                                    value={sources.filter(s => (s.Source_Id || s.source_Id) === form.SourceId).map(s => ({ value: s.Source_Id || s.source_Id, label: s.SourceName || s.sourceName }))[0] || null}
                                                    onChange={(opt) => handleFormChange("SourceId", opt?.value || null)}
                                                    styles={selectStyles}
                                                    placeholder="Select Source"
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Details</Label>
                                                <Input
                                                    type="textarea"
                                                    value={form.Details || ""}
                                                    onChange={(e) => handleFormChange("Details", e.target.value)}
                                                    placeholder="Enter campaign details"
                                                    rows="3"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={4}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Valid From <span className="text-danger">*</span></Label>
                                                <InputGroup>
                                                    <span className="input-group-text"><Calendar size={16} /></span>
                                                    <Flatpickr
                                                        className="form-control"
                                                        placeholder="dd-mm-yyyy"
                                                        options={{
                                                            dateFormat: "d-m-Y",
                                                            defaultDate: form.ValidFrom
                                                        }}
                                                        onChange={(date) => handleFormChange("ValidFrom", date)}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Valid To <span className="text-danger">*</span></Label>
                                                <InputGroup>
                                                    <span className="input-group-text"><Calendar size={16} /></span>
                                                    <Flatpickr
                                                        className="form-control"
                                                        placeholder="dd-mm-yyyy"
                                                        options={{
                                                            dateFormat: "d-m-Y",
                                                            defaultDate: form.ValidTo
                                                        }}
                                                        onChange={(date) => handleFormChange("ValidTo", date)}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup className="mb-3">
                                                <Label className="fw-bold">Amount <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={form.Amount}
                                                    onChange={(e) => handleFormChange("Amount", parseFloat(e.target.value) || 0)}
                                                    placeholder="0.00"
                                                    required
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="d-flex gap-2 mt-4">
                                      <Button
                                        type="submit"
                                        color="primary"
                                        className="btn-label"
                                        disabled={saveLoading}
                                      >
                                        <i className="bx bx-save label-icon"></i>
                                        {saveLoading ? "Saving..." : isEditing ? "Update Campaign" : "Save Campaign"}
                                      </Button>
                                      {isEditing && (
                                        <Button
                                          type="button"
                                          color="secondary"
                                          onClick={handleCancelEdit}
                                        >
                                          Cancel
                                        </Button>
                                      )}
                                    </div>
                                </Form>
                            </div>
                          )}
                        </TabPane>

                        <TabPane tabId="2">
                          <div className="p-2">
                             <div className="table-responsive">
                                <Table hover bordered className="align-middle table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Sr.No</th>
                                            <th>Company/Branch</th>
                                            <th>Campaign Name</th>
                                            <th>Source</th>
                                            <th>Valid Range</th>
                                            <th className="text-end">Amount</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={7} className="text-center py-5">
                                                    <ClipLoader color="#556ee6" size={30} />
                                                    <p className="mt-2 text-muted">Loading campaigns...</p>
                                                </td>
                                            </tr>
                                        ) : campaigns.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="text-center py-4 text-muted">
                                                    No campaigns found
                                                </td>
                                            </tr>
                                        ) : (
                                            campaigns.map((camp, index) => (
                                                <tr key={camp.campaignId || camp.CampaignId}>
                                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                                    <td>
                                                        <div className="fw-bold"><Building size={14} className="me-1 text-muted" />{getBusinessName(camp.businessId || camp.BusinessId)}</div>
                                                        <div className="small text-muted"><MapPin size={12} className="me-1" />{getBranchName(camp.branchId || camp.BranchId)}</div>
                                                    </td>
                                                    <td className="fw-medium text-primary">
                                                        {camp.campaignName || camp.CampaignName}
                                                    </td>
                                                    <td>{getSourceName(camp.sourceId || camp.SourceId)}</td>
                                                    <td>
                                                        <div className="small">
                                                            <Calendar size={12} className="me-1 text-info" />
                                                            {new Date(camp.validFrom || camp.ValidFrom).toLocaleDateString()} - {new Date(camp.validTo || camp.ValidTo).toLocaleDateString()}
                                                        </div>
                                                    </td>
                                                    <td className="fw-bold text-success text-end">
                                                        {(camp.amount || camp.Amount || 0).toFixed(2)}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <Button
                                                                color="soft-primary"
                                                                size="sm"
                                                                onClick={() => handleViewDetails(camp)}
                                                                className="btn-rounded"
                                                                title="View Details"
                                                            >
                                                                <Eye size={14} />
                                                            </Button>
                                                            {hasEditOrHigher && (
                                                                <Button
                                                                    color="soft-info"
                                                                    size="sm"
                                                                    onClick={() => handleEdit(camp)}
                                                                    className="btn-rounded"
                                                                    title="Edit"
                                                                >
                                                                    <SquarePen size={14} />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                             </div>

                             {/* Pagination */}
                             {!loading && totalCount > pageSize && (
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div className="text-muted small">
                                        Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </div>
                                    <div className="pagination pagination-rounded gap-1">
                                        <Button
                                            color="light"
                                            size="sm"
                                            disabled={page === 1}
                                            onClick={() => setPage(page - 1)}
                                        >
                                            Previous
                                        </Button>
                                        {[...Array(totalPages)].map((_, i) => (
                                            <Button
                                                key={i}
                                                color={page === i + 1 ? "primary" : "light"}
                                                size="sm"
                                                onClick={() => setPage(i + 1)}
                                            >
                                                {i + 1}
                                            </Button>
                                        ))}
                                        <Button
                                            color="light"
                                            size="sm"
                                            disabled={page === totalPages}
                                            onClick={() => setPage(page + 1)}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                             )}
                          </div>
                        </TabPane>
                      </TabContent>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
